import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import LegacyButton from '@atlaskit/button/standard-button';
import WidthDetector from '@atlaskit/width-detector';
import { RuleCard } from '@atlassian/eoc-rule-card';
import {
	Calendar,
	CalendarLoading,
	formatTimezone,
	Layer,
	LINE_WIDTH,
	NavigationMode,
	SIDE_HEADER_WIDTH,
	TimelineLoading,
} from '@atlassian/eoc-schedule-common';

import { useLayers } from '../../../controllers/list';
import {
	useCollapseUiPreference,
	useLayersUiPreference,
	useSectionUiPreferences,
} from '../../../controllers/preferences';
import { useActions as useReadyActions } from '../../../controllers/ready-store';
import { useNamesLoading } from '../../../controllers/schedule';
import { useStore as useScheduleStore } from '../../../controllers/schedule-store';
import { useTimelineEpoch } from '../../../controllers/schedule-store/helpers';
import { Container as ShiftStoreContainer } from '../../../controllers/shift-store';
import { getNumberOfDays } from '../../../utils/schedule';
import { isEmpty } from '../../../utils/validators';

import { Controls } from './controls';
import { Header } from './header';
import { messages } from './messages';
import { RotationList } from './rotation-list';
import { CalendarWrapper, Container, ExpandViewContainer, LayerName, TimeZone } from './styled';
import { type ScheduleProps } from './types';
import { WeekList } from './week-list';

const Schedule = ({ index }: ScheduleProps) => {
	const intlApi = useIntl();
	const [
		{
			selectedView,
			currentDate,
			timelineType,
			timelineData,
			calendarData,
			schedule,
			getTimelineLoading,
		},
	] = useScheduleStore();
	const { preferedLayers, layers } = useLayers({
		withForwarding: !isEmpty(timelineData?.forwardingLayers),
	});
	const [{ nowEpoch, startEpoch, endEpoch, startOffset }] = useTimelineEpoch();
	const { onScheduleReady } = useReadyActions();
	const { isCollapsed } = useCollapseUiPreference({
		id: schedule.id,
	});
	const { displayDensity } = useSectionUiPreferences();
	const { toggleShowAllLayers, showAllLayers } = useLayersUiPreference({
		id: schedule.id,
	});
	const { isLoading: isResponderNamesLoading } = useNamesLoading();

	const isScheduleLoading = useMemo(
		() => getTimelineLoading || isResponderNamesLoading,
		[getTimelineLoading, isResponderNamesLoading],
	);

	useEffect(() => {
		if (isScheduleLoading === false) {
			onScheduleReady(schedule.id);
		}
	}, [isScheduleLoading, onScheduleReady, schedule.id]);

	return (
		<RuleCard
			value={schedule.id}
			title={schedule.name}
			headingAs="h3"
			droppableId="schedules"
			actions={<Header index={index} />}
			description={
				<TimeZone>
					{formatTimezone({
						name: schedule.timezone,
						offset: schedule.utcTimeOffsetMillis,
						variant: 'long',
					})}
				</TimeZone>
			}
			gap={`${8 * 1.25}px`}
			contentSpacing="none"
		>
			<Container>
				{!isCollapsed && <Controls />}

				{!isCollapsed && selectedView === NavigationMode.Timeline && (
					<>
						<div>
							{preferedLayers.map((layer) => (
								<React.Fragment key={`fragment-${layer.value}`}>
									<LayerName>{layer.label}</LayerName>
									{isScheduleLoading ? (
										<TimelineLoading
											variant="full"
											size={displayDensity !== 'comfort' ? 'medium' : 'large'}
											numberOfDays={getNumberOfDays({
												timelineType,
												currentDate,
											})}
										/>
									) : (
										<Layer
											nowEpoch={nowEpoch}
											startEpoch={startEpoch}
											endEpoch={endEpoch}
											offset={startOffset}
											size={displayDensity !== 'comfort' ? 'medium' : 'large'}
											variant="full"
											rotationSlot={
												<WidthDetector>
													{(width) => (
														<ShiftStoreContainer
															key={`timeline-${layer.value}-${schedule.id}`}
															scope={`timeline-${layer.value}-${schedule.id}`}
															nowEpoch={nowEpoch}
														>
															<RotationList
																width={width ? width - (SIDE_HEADER_WIDTH + LINE_WIDTH) : 0}
																nowEpoch={nowEpoch}
																startEpoch={startEpoch}
																endEpoch={endEpoch}
																rotations={timelineData?.[layer.value] ?? []}
																schedule={schedule}
																layer={layer}
															/>
														</ShiftStoreContainer>
													)}
												</WidthDetector>
											}
										/>
									)}
								</React.Fragment>
							))}
						</div>

						<ExpandViewContainer>
							<LegacyButton spacing="none" appearance="link" onClick={() => toggleShowAllLayers()}>
								{intlApi.formatMessage(
									showAllLayers ? messages.showLessLayers : messages.showAllLayers,
								)}
							</LegacyButton>
						</ExpandViewContainer>
					</>
				)}

				{!isCollapsed && selectedView === NavigationMode.Calendar && (
					<CalendarWrapper>
						{isScheduleLoading ? (
							<CalendarLoading />
						) : (
							<Calendar nowEpoch={nowEpoch} startEpoch={startEpoch} offset={startOffset}>
								<WidthDetector>
									{(width) => (
										<ShiftStoreContainer
											key={`calendar-${layers.final.value}-${schedule.id}`}
											scope={`calendar-${layers.final.value}-${schedule.id}`}
											nowEpoch={nowEpoch}
										>
											<WeekList
												width={width ?? 0}
												nowEpoch={nowEpoch}
												offset={startOffset}
												weeks={calendarData?.finalWeeks ?? []}
												schedule={schedule}
												layer={layers.final}
											/>
										</ShiftStoreContainer>
									)}
								</WidthDetector>
							</Calendar>
						)}
					</CalendarWrapper>
				)}
			</Container>
		</RuleCard>
	);
};

export { Schedule };
